import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronDown, ChevronRight, Package, Play, ShoppingBag, Loader, Tag, ArrowLeft,
  Check, X, ChevronLeft, RefreshCw, Clock, SkipForward,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '@libero/core/services/api';
import FlowResultPanel from '@libero/core/components/flow/FlowResultPanel';
import { FlowExecutionDetail } from '@libero/core/components/flow';
import { useFlowExecution } from '@libero/core/hooks/useFlowExecution';
import PoolManager from './PoolManager';
import {
  suggestCategories, findCategoryById, namesDiffer,
  MIN_QUERY_LENGTH, CategoryListItem, CategorySuggestion,
} from './categorySuggest';
import { mergeVariantSelection, pruneItemMap } from './wizardSelection';

// =========================================================================
// Types
// =========================================================================

interface Attribute {
  attributeId: number;
  attributeValueId: number;
  valueName: string;
  attributeName: string;
}

interface ChildVariation {
  id: number;
  number: string;
  attributes: Attribute[];
  isActive: boolean;
}

interface PreviewItem {
  itemId: number;
  name: string;
  imageUrl: string | null;
  mainVariation: {
    id: number;
    number: string;
    salesPrices: { salesPriceId: number; price: number }[];
  };
  childVariations: ChildVariation[];
}

/** Eine Stichprobe der Rabatt-Rueckrechnung (GET /rabatt-vorschlag). */
interface RabattStichprobe {
  itemId: number;
  variationId: number;
  name: string;
  number: string;
  webPreis: number;
  aufpreis: number;
  uvpQuelle: number | null;
  impliziertPct: number | null;
  kandidaten?: number[];
  geteilt?: boolean;
  grund?: string;
}

interface RabattVorschlagDaten {
  vorschlagPct: number | null;
  medianPct: number | null;
  gestuetztVon: number;
  verlaesslichkeit: 'hoch' | 'mittel' | 'gering' | 'keine';
  auswertbar: number;
  nurGeteilteArtikel: boolean;
  stichproben: RabattStichprobe[];
}

/** Druckaufpreis-Bestelleigenschaft (115–118), live aus GET /sponsor-properties geladen. */
interface SponsorProperty {
  propertyId: number;
  name: string;
  surcharge: number;
  groupId: number;
}

interface TeamshopConfig {
  name: string;
  kategorieId: string;
  rabatt: string;
  tagIds: string;
  selectedVariants: Map<number, Set<number>>;
  /** itemId -> Set gewaehlter Druckaufpreis-propertyIds (115-118), analog selectedVariants. */
  druckProperties: Map<number, Set<number>>;
}

/** PRICE-CHECK-01: Ergebnis der Preis-Nachkontrolle aus dem Batch (rein informativ). */
interface PriceCheckInfo {
  geprueft: number;
  ok: number;
  ohneAufpreis: number;
  fehlt: number;
  abweichend: number;
  /** Artikel die gar nicht geprüft werden konnten — zählt bewusst als auffällig. */
  ungeprueft: number;
  auffaellig: boolean;
  fehler?: string;
}

/** COMPLETENESS-01: Rückkontrolle von Bildern + Variantenaktivierung (backend/src/postRunChecks.ts). */
interface CompletenessCheckInfo {
  geprueft?: number;
  bilderFehlt?: number;
  bilderTeilweise?: number;
  kindvariantenInaktiv?: number;
  hauptvarianteAktiv?: number;
  keineKindvarianten?: number;
  ungeprueft?: number;
  auffaellig?: boolean;
  fehler?: string;
}

interface BatchItemProgress {
  index: number;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'timeout' | 'skipped';
  executionId?: string;
  error?: string;
  priceCheck?: PriceCheckInfo;
  completenessCheck?: CompletenessCheckInfo;
}

interface BatchProgress {
  batchId: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled' | 'partial_failure';
  total: number;
  completedCount: number;
  failedCount: number;
  current: {
    index: number;
    name: string;
    stepName: string;
    stepIndex: number;
    stepTotal: number;
    variantDetail?: string;
  } | null;
  teamshops: BatchItemProgress[];
}

// Linearer Ablauf (UX-Vereinfachung 2026-07-30): ein Teamshop, vier Schritte + Fortschritt.
//   parameter → articles (Pool suchen/übernehmen) → colors (Farben & Veredelung) → review → progress
type WizardStep = 'parameter' | 'articles' | 'colors' | 'review' | 'progress';

const STEP_LABELS: { key: WizardStep; label: string }[] = [
  { key: 'parameter', label: 'Teamshop' },
  { key: 'articles', label: 'Artikel' },
  { key: 'colors', label: 'Farben & Veredelung' },
  { key: 'review', label: 'Übersicht' },
];

const BATCH_ID_KEY = 'teamshopBatchId';

// =========================================================================
// Color Badge Helpers
// =========================================================================

const COLOR_MAP: Record<string, { bg: string; color: string }> = {
  schwarz:  { bg: '#1a1a1a', color: '#fff' },
  weiß:     { bg: '#f3f4f6', color: '#1a1a1a' },
  weiss:    { bg: '#f3f4f6', color: '#1a1a1a' },
  rot:      { bg: '#ef4444', color: '#fff' },
  blau:     { bg: '#3b82f6', color: '#fff' },
  royal:    { bg: '#2563eb', color: '#fff' },
  grün:     { bg: '#22c55e', color: '#fff' },
  gruen:    { bg: '#22c55e', color: '#fff' },
  gelb:     { bg: '#eab308', color: '#1a1a1a' },
  orange:   { bg: '#f97316', color: '#fff' },
  lila:     { bg: '#a855f7', color: '#fff' },
  pink:     { bg: '#ec4899', color: '#fff' },
  grau:     { bg: '#9ca3af', color: '#fff' },
  silber:   { bg: '#c0c0c0', color: '#1a1a1a' },
  gold:     { bg: '#d4af37', color: '#1a1a1a' },
  navy:     { bg: '#1e3a5f', color: '#fff' },
  marine:   { bg: '#1e3a5f', color: '#fff' },
  bordeaux: { bg: '#7c2d12', color: '#fff' },
  braun:    { bg: '#92400e', color: '#fff' },
  beige:    { bg: '#d2b48c', color: '#1a1a1a' },
  hellblau: { bg: '#7dd3fc', color: '#1a1a1a' },
  türkis:   { bg: '#14b8a6', color: '#fff' },
  turkis:   { bg: '#14b8a6', color: '#fff' },
  // --- Englische Keys (fuer Plenty-Attribute) ---
  // Multi-Word zuerst (muessen vor generischen stehen)
  'dark green':  { bg: '#15803d', color: '#fff' },
  'light blue':  { bg: '#93c5fd', color: '#1a1a1a' },
  'light grey':  { bg: '#d1d5db', color: '#1a1a1a' },
  'light gray':  { bg: '#d1d5db', color: '#1a1a1a' },
  // Generische Single-Word
  black:   { bg: '#1a1a1a', color: '#fff' },
  white:   { bg: '#f3f4f6', color: '#1a1a1a' },
  red:     { bg: '#dc2626', color: '#fff' },
  green:   { bg: '#16a34a', color: '#fff' },
  yellow:  { bg: '#eab308', color: '#1a1a1a' },
  blue:    { bg: '#3b82f6', color: '#fff' },
  grey:    { bg: '#9ca3af', color: '#fff' },
  gray:    { bg: '#9ca3af', color: '#fff' },
  purple:  { bg: '#a855f7', color: '#fff' },
  silver:  { bg: '#c0c0c0', color: '#1a1a1a' },
  maroon:  { bg: '#800000', color: '#fff' },
  carbon:  { bg: '#374151', color: '#fff' },
  mint:    { bg: '#6ee7b7', color: '#1a1a1a' },
  solar:   { bg: '#facc15', color: '#1a1a1a' },
};

function getColorStyle(valueName: string): { bg: string; color: string } {
  const key = valueName.toLowerCase();
  for (const [k, v] of Object.entries(COLOR_MAP)) {
    if (key.includes(k)) return v;
  }
  let hash = 0;
  for (let i = 0; i < valueName.length; i++) {
    hash = valueName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const palette = ['#6366f1', '#8b5cf6', '#06b6d4', '#14b8a6', '#f59e0b', '#10b981'];
  return { bg: palette[Math.abs(hash) % palette.length], color: '#fff' };
}

function getDualColorStyle(colorName: string): { c1: { bg: string; color: string }; c2: { bg: string; color: string } | null; isSplit: boolean } {
  const parts = colorName.split('/');
  const c1 = getColorStyle(parts[0].trim());
  if (parts.length < 2) return { c1, c2: null, isSplit: false };
  const c2 = getColorStyle(parts[1].trim());
  return { c1, c2, isSplit: true };
}

function getVariantColor(cv: ChildVariation): string | null {
  for (const attr of cv.attributes) {
    const n = attr.attributeName.toLowerCase();
    if (n.includes('farbe') || n.includes('color') || n.includes('colour')) return attr.valueName;
  }
  for (const attr of cv.attributes) {
    const key = attr.valueName.toLowerCase();
    for (const k of Object.keys(COLOR_MAP)) {
      if (key.includes(k)) return attr.valueName;
    }
  }
  return null;
}

function getColorGroups(item: PreviewItem): Map<string, number[]> {
  const map = new Map<string, number[]>();
  for (const cv of item.childVariations) {
    const color = getVariantColor(cv);
    const key = color ?? 'Sonstige';
    const existing = map.get(key) ?? [];
    existing.push(cv.id);
    map.set(key, existing);
  }
  return map;
}

function createEmptyTeamshop(): TeamshopConfig {
  return { name: '', kategorieId: '', rabatt: '', tagIds: '', selectedVariants: new Map(), druckProperties: new Map() };
}

/** UVP (salesPriceId=16) aus den Verkaufspreisen eines Artikels extrahieren (Frontend-Spiegel). */
function extractUvpFrontend(item: PreviewItem): number {
  return item.mainVariation.salesPrices.find(p => p.salesPriceId === 16)?.price ?? 0;
}

/**
 * Anzeige-Spiegel von `calculateMainPrice` (backend/src/flows/teamshop-artikelanlage.flow.ts).
 * Webshop-Preis = UVP × (1 − Rabatt%) auf 2 Dezimalstellen, KEIN CEILING/Aufrunden
 * (User-Entscheidung 2026-07-21). NUR für die review-Step-Transparenz (UI-01) — der
 * tatsächliche Preis wird vom Flow gesetzt. Bei Formeländerung im Flow synchron halten (T-01-07).
 */
function computeMainPriceDisplay(uvp: number, rabatt: number): number {
  return parseFloat((uvp * (1 - rabatt / 100)).toFixed(2));
}

/** Deutsches Zahlenformat: 25.5 -> "25,50 €" */
function formatEuro(n: number): string {
  return n.toFixed(2).replace('.', ',') + ' €';
}

/**
 * Erkennt den Boot-Cleanup-Fehler: Der Job lief noch, als der Server neu gestartet wurde
 * (typisch bei einem Deployment). Das ist KEIN Fehler im Teamshop — der Lauf lässt sich
 * gefahrlos erneut starten (Flow ist idempotent).
 */
function isServerRestartError(msg?: string | null): boolean {
  return !!msg && /server-restart|unsanft beendet|verwaist/i.test(msg);
}

// =========================================================================
// ArticleRow (unchanged from original)
// =========================================================================

const ArticleRow: React.FC<{
  item: PreviewItem;
  expanded: boolean;
  selectedSet: Set<number>;
  onToggleExpand: () => void;
  onToggleColorGroup: (variantIds: number[], allSelected: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  sponsorProperties: SponsorProperty[];
  selectedPropertyIds: Set<number>;
  onTogglePropertyId: (propertyId: number) => void;
}> = ({
  item, expanded, selectedSet, onToggleExpand, onToggleColorGroup, onSelectAll, onDeselectAll,
  sponsorProperties, selectedPropertyIds, onTogglePropertyId,
}) => {
  const checkRef = useRef<HTMLInputElement>(null);

  const total = item.childVariations.length;
  const numSelected = selectedSet.size;
  const allSelected = total === 0 ? numSelected > 0 : numSelected === total;
  const partiallySelected = total > 0 && numSelected > 0 && numSelected < total;
  const anySelected = numSelected > 0;

  React.useEffect(() => {
    if (checkRef.current) {
      checkRef.current.indeterminate = partiallySelected;
    }
  }, [partiallySelected]);

  const uvp = (() => {
    const sp = item.mainVariation.salesPrices.find(p => p.salesPriceId === 16);
    if (!sp) return '-';
    return sp.price.toFixed(2).replace('.', ',') + ' €';
  })();

  const colorGroups = getColorGroups(item);

  return (
    <div style={{
      border: `1px solid ${anySelected ? '#c8dd40' : '#e5e7eb'}`,
      borderRadius: 8,
      overflow: 'hidden',
      transition: 'border-color 0.2s',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px',
        background: anySelected ? '#fafff0' : '#fff',
      }}>
        <input
          ref={checkRef}
          type="checkbox"
          checked={allSelected}
          onChange={() => (allSelected || partiallySelected ? onDeselectAll() : onSelectAll())}
          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#c8dd40', flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1a1a' }}>{item.name}</div>
          <div style={{ display: 'flex', gap: 12, fontSize: '0.75rem', color: '#6b7280', marginTop: 2 }}>
            <span>#{item.mainVariation.number}</span>
            <span>UVP: <strong style={{ color: '#1a1a1a' }}>{uvp}</strong></span>
            <span>{total === 0 ? 'Nur Hauptvariante' : `${numSelected}/${total} Variante${total !== 1 ? 'n' : ''}`}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button onClick={e => { e.stopPropagation(); onSelectAll(); }} style={{
            padding: '2px 8px', fontSize: '0.7rem', border: '1px solid #d1d5db',
            borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151',
          }}>Alle</button>
          <button onClick={e => { e.stopPropagation(); onDeselectAll(); }} style={{
            padding: '2px 8px', fontSize: '0.7rem', border: '1px solid #d1d5db',
            borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151',
          }}>Keine</button>
        </div>
        <button onClick={onToggleExpand} style={{
          background: 'none', padding: 4, color: '#9ca3af', cursor: 'pointer', flexShrink: 0, border: 'none',
        }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>

      {anySelected && sponsorProperties.length > 0 && (
        <div style={{
          padding: '8px 14px 10px', background: '#fffef2',
          borderTop: '1px solid #f3f4f6', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        }}>
          <span style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 600 }}>Druckaufpreise:</span>
          {sponsorProperties.map(sp => {
            const checked = selectedPropertyIds.has(sp.propertyId);
            return (
              <label key={sp.propertyId} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                fontSize: '0.78rem', color: '#1a1a1a', cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onTogglePropertyId(sp.propertyId)}
                  style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#c8dd40' }}
                />
                {sp.name} (+{sp.surcharge.toFixed(2).replace('.', ',')} €)
              </label>
            );
          })}
        </div>
      )}

      {expanded && (
        <div style={{
          padding: '10px 14px 12px', background: '#f9fafb',
          borderTop: '1px solid #f3f4f6', display: 'flex', flexWrap: 'wrap', gap: 6,
        }}>
          {item.childVariations.length === 0 ? (
            <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Keine Kindvarianten vorhanden</span>
          ) : (
            Array.from(colorGroups.entries()).map(([colorName, variantIds]) => {
              const { c1, c2, isSplit } = getDualColorStyle(colorName);
              const allColorSelected = variantIds.every(id => selectedSet.has(id));
              const someColorSelected = variantIds.some(id => selectedSet.has(id));
              const isActive = allColorSelected;
              const isPartial = !allColorSelected && someColorSelected;
              const selected = isActive || isPartial;
              const bgStyle = isSplit && selected
                ? { background: `linear-gradient(135deg, ${c1.bg} 50%, ${c2!.bg} 50%)` }
                : selected
                  ? { background: c1.bg }
                  : { background: '#e5e7eb' };
              return (
                <button key={colorName} onClick={() => onToggleColorGroup(variantIds, allColorSelected)}
                  title={variantIds.length > 1 ? `${variantIds.length} Größen` : '1 Größe'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '4px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
                    cursor: 'pointer', transition: 'opacity 0.15s, transform 0.1s',
                    ...bgStyle,
                    color: selected ? '#fff' : '#9ca3af',
                    textShadow: isSplit && selected ? '0 0 3px rgba(0,0,0,0.5)' : 'none',
                    border: `2px solid ${selected ? c1.bg : '#d1d5db'}`,
                    opacity: isActive ? 1 : isPartial ? 0.75 : 0.55,
                    transform: selected ? 'scale(1)' : 'scale(0.96)',
                    textDecoration: selected ? 'none' : 'line-through',
                  }}>
                  {colorName}
                  <span style={{ fontSize: '0.68rem', fontWeight: 400, opacity: 0.85 }}>({variantIds.length})</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// =========================================================================
// ArticleSelectorPanel — controlled, shareable across tabs
// =========================================================================

interface ArticleSelectorPanelProps {
  items: PreviewItem[];
  loadingItems: boolean;
  selectedVariants: Map<number, Set<number>>;
  onSelectedVariantsChange: (map: Map<number, Set<number>>) => void;
  onLoadItems: () => void;
  sponsorProperties: SponsorProperty[];
  druckProperties: Map<number, Set<number>>;
  onDruckPropertiesChange: (map: Map<number, Set<number>>) => void;
}

const ArticleSelectorPanel: React.FC<ArticleSelectorPanelProps> = ({
  items, loadingItems, selectedVariants, onSelectedVariantsChange, onLoadItems,
  sponsorProperties, druckProperties, onDruckPropertiesChange,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    setExpandedItems(new Set(items.map(i => i.itemId)));
  }, [items]);

  const toggleExpand = (itemId: number) =>
    setExpandedItems(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });

  const toggleColorGroup = (itemId: number, variantIds: number[], allSelected: boolean) => {
    const next = new Map(selectedVariants);
    const set = new Set(next.get(itemId) || []);
    if (allSelected) { variantIds.forEach(id => set.delete(id)); }
    else { variantIds.forEach(id => set.add(id)); }
    next.set(itemId, set);
    onSelectedVariantsChange(next);
  };

  const selectAll = (item: PreviewItem) => {
    const next = new Map(selectedVariants);
    next.set(item.itemId, item.childVariations.length > 0
      ? new Set(item.childVariations.map(cv => cv.id))
      : new Set([item.mainVariation.id]));
    onSelectedVariantsChange(next);
  };

  const deselectAll = (item: PreviewItem) => {
    const next = new Map(selectedVariants);
    next.set(item.itemId, new Set());
    onSelectedVariantsChange(next);
  };

  const togglePropertyId = (itemId: number, propertyId: number) => {
    const next = new Map(druckProperties);
    const set = new Set(next.get(itemId) || []);
    if (set.has(propertyId)) set.delete(propertyId); else set.add(propertyId);
    next.set(itemId, set);
    onDruckPropertiesChange(next);
  };

  const globalColorInfo = useMemo(() => {
    const map = new Map<string, { variantsByItem: Map<number, number[]>; totalArticles: number }>();
    for (const item of items) {
      for (const [colorName, variantIds] of getColorGroups(item).entries()) {
        if (colorName === 'Sonstige') continue;
        if (!map.has(colorName)) map.set(colorName, { variantsByItem: new Map(), totalArticles: 0 });
        const entry = map.get(colorName)!;
        entry.variantsByItem.set(item.itemId, variantIds);
        entry.totalArticles += 1;
      }
    }
    return map;
  }, [items]);

  const getGlobalColorStatus = (colorName: string): 'none' | 'partial' | 'full' => {
    const entry = globalColorInfo.get(colorName);
    if (!entry) return 'none';
    let selectedCount = 0, totalCount = 0;
    for (const [itemId, variantIds] of entry.variantsByItem.entries()) {
      const sel = selectedVariants.get(itemId) || new Set<number>();
      totalCount += variantIds.length;
      selectedCount += variantIds.filter(id => sel.has(id)).length;
    }
    if (selectedCount === 0) return 'none';
    if (selectedCount === totalCount) return 'full';
    return 'partial';
  };

  const toggleGlobalColor = (colorName: string) => {
    const status = getGlobalColorStatus(colorName);
    const entry = globalColorInfo.get(colorName);
    if (!entry) return;
    const next = new Map(selectedVariants);
    for (const [itemId, variantIds] of entry.variantsByItem.entries()) {
      const set = new Set(next.get(itemId) || []);
      if (status === 'none') { variantIds.forEach(id => set.add(id)); }
      else { variantIds.forEach(id => set.delete(id)); }
      next.set(itemId, set);
    }
    onSelectedVariantsChange(next);
  };

  const selectAllColors = () => {
    const next = new Map(selectedVariants);
    for (const entry of globalColorInfo.values()) {
      for (const [itemId, variantIds] of entry.variantsByItem.entries()) {
        const set = new Set(next.get(itemId) || []);
        variantIds.forEach(id => set.add(id));
        next.set(itemId, set);
      }
    }
    onSelectedVariantsChange(next);
  };

  const deselectAllColors = () => {
    const next = new Map(selectedVariants);
    for (const item of items) next.set(item.itemId, new Set());
    onSelectedVariantsChange(next);
  };

  // Druckaufpreis-Eigenschaft (115–118) für ALLE Artikel gesammelt setzen/entfernen — analog
  // "Farben filtern" oben. Status: none (kein Artikel) / partial / full (alle Artikel).
  const getGlobalDruckStatus = (propertyId: number): 'none' | 'partial' | 'full' => {
    if (items.length === 0) return 'none';
    let count = 0;
    for (const item of items) {
      if ((druckProperties.get(item.itemId) || new Set<number>()).has(propertyId)) count++;
    }
    if (count === 0) return 'none';
    if (count === items.length) return 'full';
    return 'partial';
  };

  const toggleGlobalDruck = (propertyId: number) => {
    const status = getGlobalDruckStatus(propertyId);
    const next = new Map(druckProperties);
    for (const item of items) {
      const set = new Set(next.get(item.itemId) || []);
      if (status === 'full') set.delete(propertyId); else set.add(propertyId);
      next.set(item.itemId, set);
    }
    onDruckPropertiesChange(next);
  };

  const selectedItemCount = [...selectedVariants.values()].filter(s => s.size > 0).length;
  const selectedVariantTotal = [...selectedVariants.values()].reduce((sum, s) => sum + s.size, 0);

  return (
    <div className="card" style={{ margin: 0 }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="card-title" style={{ fontSize: '0.95rem', margin: 0 }}>
          Artikel-Auswahl
          {items.length > 0 && (
            <span style={{ marginLeft: 8, fontSize: '0.78rem', fontWeight: 400, color: '#6b7280' }}>
              {selectedItemCount}/{items.length} Artikel · {selectedVariantTotal} Varianten
            </span>
          )}
        </h3>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: '0.85rem' }}
          onClick={onLoadItems} disabled={loadingItems}>
          {loadingItems
            ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Lädt...</>
            : <><Package size={14} /> {items.length > 0 ? 'Neu laden' : 'Artikel laden'}</>
          }
        </button>
      </div>

      {items.length === 0 && (
        <div style={{ padding: '48px 0', textAlign: 'center', color: '#9ca3af' }}>
          {loadingItems ? (
            <>
              <Loader size={32} style={{ display: 'block', margin: '0 auto 12px', animation: 'spin 1s linear infinite', color: '#c8dd40' }} />
              <p style={{ margin: 0, fontSize: '0.875rem' }}>Artikel werden geladen…</p>
            </>
          ) : (
            <>
              <Package size={40} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }} />
              <p style={{ margin: 0, fontSize: '0.875rem' }}>Klicke auf „Artikel laden" um fortzufahren</p>
            </>
          )}
        </div>
      )}
      {items.length > 0 && (
        <>
          {globalColorInfo.size > 0 && (
            <div style={{
              background: '#f8fafc', border: '1px solid #e5e7eb',
              borderRadius: 8, padding: '12px 14px', marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>Farben filtern</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={selectAllColors} style={{
                    padding: '2px 8px', fontSize: '0.7rem', border: '1px solid #d1d5db',
                    borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151',
                  }}>Alle Farben</button>
                  <button onClick={deselectAllColors} style={{
                    padding: '2px 8px', fontSize: '0.7rem', border: '1px solid #d1d5db',
                    borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151',
                  }}>Keine</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {Array.from(globalColorInfo.entries()).map(([colorName, entry]) => {
                  const status = getGlobalColorStatus(colorName);
                  const { c1, c2, isSplit } = getDualColorStyle(colorName);
                  const isActive = status === 'full';
                  const isPartial = status === 'partial';
                  const selected = isActive || isPartial;
                  const bgStyle = isSplit && selected
                    ? { background: `linear-gradient(135deg, ${c1.bg} 50%, ${c2!.bg} 50%)` }
                    : selected
                      ? { background: c1.bg }
                      : { background: '#e5e7eb' };
                  return (
                    <button key={colorName} onClick={() => toggleGlobalColor(colorName)}
                      title={`${entry.totalArticles} Artikel`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '5px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600,
                        cursor: 'pointer', transition: 'opacity 0.15s, transform 0.1s',
                        ...bgStyle,
                        color: selected ? '#fff' : '#9ca3af',
                        textShadow: isSplit && selected ? '0 0 3px rgba(0,0,0,0.5)' : 'none',
                        border: `2px solid ${selected ? c1.bg : '#d1d5db'}`,
                        opacity: isActive ? 1 : isPartial ? 0.8 : 0.55,
                        transform: selected ? 'scale(1)' : 'scale(0.97)',
                      }}>
                      {colorName}
                      <span style={{ fontSize: '0.7rem', fontWeight: 400, opacity: 0.85 }}>{entry.totalArticles}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {sponsorProperties.length > 0 && (
            <div style={{
              background: '#fffef2', border: '1px solid #f0e9c0',
              borderRadius: 8, padding: '12px 14px', marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
                  Druckaufpreise <span style={{ fontWeight: 400, color: '#6b7280' }}>(für alle Artikel)</span>
                </span>
                <button onClick={() => { const next = new Map<number, Set<number>>(); onDruckPropertiesChange(next); }} style={{
                  padding: '2px 8px', fontSize: '0.7rem', border: '1px solid #d1d5db',
                  borderRadius: 4, background: '#fff', cursor: 'pointer', color: '#374151',
                }}>Keine</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {sponsorProperties.map(sp => {
                  const status = getGlobalDruckStatus(sp.propertyId);
                  const isActive = status === 'full';
                  const isPartial = status === 'partial';
                  const selected = isActive || isPartial;
                  return (
                    <button key={sp.propertyId} onClick={() => toggleGlobalDruck(sp.propertyId)}
                      title={`Für alle ${items.length} Artikel setzen/entfernen`}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '5px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600,
                        cursor: 'pointer', transition: 'opacity 0.15s, transform 0.1s',
                        background: isActive ? '#c8dd40' : isPartial ? '#e6ee9c' : '#e5e7eb',
                        color: selected ? '#1a1a1a' : '#9ca3af',
                        border: `2px solid ${selected ? '#c8dd40' : '#d1d5db'}`,
                        opacity: isActive ? 1 : isPartial ? 0.85 : 0.6,
                        transform: selected ? 'scale(1)' : 'scale(0.97)',
                      }}>
                      {sp.name || `ID ${sp.propertyId}`}
                      <span style={{ fontSize: '0.7rem', fontWeight: 400, opacity: 0.85 }}>
                        +{sp.surcharge.toFixed(2).replace('.', ',')} €
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map(item => (
              <ArticleRow
                key={item.itemId}
                item={item}
                expanded={expandedItems.has(item.itemId)}
                selectedSet={selectedVariants.get(item.itemId) || new Set()}
                onToggleExpand={() => toggleExpand(item.itemId)}
                onToggleColorGroup={(ids, allSel) => toggleColorGroup(item.itemId, ids, allSel)}
                onSelectAll={() => selectAll(item)}
                onDeselectAll={() => deselectAll(item)}
                sponsorProperties={sponsorProperties}
                selectedPropertyIds={druckProperties.get(item.itemId) || new Set()}
                onTogglePropertyId={(propertyId) => togglePropertyId(item.itemId, propertyId)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// =========================================================================
// Progress Step
// =========================================================================

const ProgressStep: React.FC<{
  progress: BatchProgress | null;
  batchId: string;
  currentExecId: string | null;
  onCancel: () => void;
  showCancelConfirm: boolean;
  onCancelConfirm: () => void;
  onCancelDismiss: () => void;
  cancelling: boolean;
  onNewBatch: () => void;
}> = ({ progress, batchId, currentExecId, onCancel, showCancelConfirm, onCancelConfirm, onCancelDismiss, cancelling, onNewBatch }) => {
  const [detailExecutionId, setDetailExecutionId] = useState<string | null>(null);
  const isDone = progress && ['completed', 'failed', 'cancelled', 'partial_failure'].includes(progress.status);
  const isRunning = progress?.status === 'running';
  // Wurde (mind.) ein Teamshop durch einen Server-Neustart abgebrochen? Dann freundlich erklären
  // statt nur „fehlgeschlagen" — kein Teamshop-Fehler, einfach erneut starten (idempotent).
  const wasServerRestart = !!progress && (progress.teamshops || []).some(ts => isServerRestartError(ts.error));

  const batchPct = progress ? Math.round((progress.completedCount / progress.total) * 100) : 0;

  const statusIcon = (s: BatchItemProgress['status']) => {
    if (s === 'completed') return <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Check size={12} color="#065F46" /></div>;
    if (s === 'failed') return <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><X size={12} color="#991B1B" /></div>;
    if (s === 'timeout') return <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Clock size={12} color="#92400E" /></div>;
    if (s === 'skipped') return <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><SkipForward size={12} color="#6B7280" /></div>;
    if (s === 'running') return <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><RefreshCw size={12} color="#92400E" style={{ animation: 'spin 1s linear infinite' }} /></div>;
    return <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><div style={{ width: 6, height: 6, borderRadius: '50%', background: '#9CA3AF' }} /></div>;
  };

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      {!progress ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
          <p style={{ margin: 0 }}>Verbinde mit Batch {batchId}…</p>
        </div>
      ) : (
        <>
          {/* Level 1: Batch overall */}
          <div className="card" style={{ margin: '0 0 16px' }}>
            <div style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                  Batch-Fortschritt
                </span>
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                  {progress.completedCount + progress.failedCount}/{progress.total} Teamshops
                </span>
              </div>
              <div style={{ height: 12, background: '#e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 6,
                  background: progress.failedCount > 0 ? '#f97316' : '#c8dd40',
                  width: `${batchPct}%`, transition: 'width 0.4s ease',
                }} />
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: '0.78rem', color: '#6b7280' }}>
                <span style={{ color: '#065F46' }}>✓ {progress.completedCount} fertig</span>
                {progress.failedCount > 0 && <span style={{ color: '#991B1B' }}>✗ {progress.failedCount} fehlgeschlagen</span>}
                <span>{progress.total - progress.completedCount - progress.failedCount} ausstehend</span>
              </div>
            </div>
          </div>

          {/* Level 2: Aktuelle Execution via FlowResultPanel (Core-Hook) */}
          {currentExecId && isRunning && (
            <div style={{ margin: '0 0 16px' }}>
              {progress.current && (
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>
                  {progress.current.name}
                </div>
              )}
              <FlowResultPanel
                flowId="teamshop-artikelanlage"
                executionId={currentExecId}
              />
            </div>
          )}

          {/* Teamshop list */}
          <div className="card" style={{ margin: '0 0 16px' }}>
            <div style={{ padding: '14px 20px' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '0.9rem', fontWeight: 600 }}>Teamshops</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(progress.teamshops || []).map((ts) => (
                  <div key={ts.index} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 10px', borderRadius: 6,
                    background: ts.status === 'running' ? '#fefce8' : ts.status === 'failed' ? '#fef2f2' : ts.status === 'completed' ? '#f0fdf4' : '#f9fafb',
                  }}>
                    {statusIcon(ts.status)}
                    <span style={{ flex: 1, fontSize: '0.87rem', fontWeight: 500 }}>
                      {ts.index + 1}. {ts.name}
                    </span>
                    {ts.error && (
                      <span style={{ fontSize: '0.72rem', color: '#991B1B', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {isServerRestartError(ts.error) ? 'Durch Server-Neustart abgebrochen' : ts.error}
                      </span>
                    )}
                    {/* PRICE-CHECK-01: unabhaengige Preis-Nachkontrolle. Rein informativ —
                        der Batch-Status wird davon nicht beeinflusst. */}
                    {ts.priceCheck && (
                      <span
                        title={
                          ts.priceCheck.fehler
                            ? ts.priceCheck.fehler
                            : `${ts.priceCheck.geprueft} Variationen geprüft · ${ts.priceCheck.ok} korrekt` +
                              (ts.priceCheck.ohneAufpreis ? ` · ${ts.priceCheck.ohneAufpreis} ohne Aufpreis` : '') +
                              (ts.priceCheck.fehlt ? ` · ${ts.priceCheck.fehlt} ohne Preis` : '') +
                              (ts.priceCheck.abweichend ? ` · ${ts.priceCheck.abweichend} abweichend` : '') +
                              (ts.priceCheck.ungeprueft ? ` · ${ts.priceCheck.ungeprueft} Artikel UNGEPRÜFT` : '')
                        }
                        style={{
                          fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, flexShrink: 0,
                          border: `1px solid ${ts.priceCheck.fehler ? '#DAD9D9' : ts.priceCheck.auffaellig ? '#fca5a5' : '#86efac'}`,
                          background: ts.priceCheck.fehler ? '#f9fafb' : ts.priceCheck.auffaellig ? '#fef2f2' : '#f0fdf4',
                          color: ts.priceCheck.fehler ? '#1A1A1A' : ts.priceCheck.auffaellig ? '#991B1B' : '#065F46',
                        }}>
                        {ts.priceCheck.fehler
                          ? 'Preise ungeprüft'
                          : ts.priceCheck.auffaellig
                            ? `Preise prüfen (${ts.priceCheck.ohneAufpreis + ts.priceCheck.fehlt + ts.priceCheck.abweichend + (ts.priceCheck.ungeprueft || 0)})`
                            : `Preise ok (${ts.priceCheck.geprueft})`}
                      </span>
                    )}
                    {/* COMPLETENESS-01: Bilder + Variantenaktivierung, aus Plenty zurueckgelesen.
                        Ebenfalls rein informativ. Eine aktive Hauptvariante ist der schwerste
                        Befund und wird deshalb im Text zuerst genannt. */}
                    {ts.completenessCheck && (() => {
                      const cc = ts.completenessCheck!;
                      const befunde = (cc.bilderFehlt || 0) + (cc.bilderTeilweise || 0)
                        + (cc.kindvariantenInaktiv || 0) + (cc.hauptvarianteAktiv || 0) + (cc.ungeprueft || 0);
                      const label = cc.fehler
                        ? 'Bilder/Aktivierung ungeprüft'
                        : cc.hauptvarianteAktiv
                          ? `HAUPTVARIANTE AKTIV (${cc.hauptvarianteAktiv})`
                          : cc.auffaellig
                            ? `Bilder/Aktivierung prüfen (${befunde})`
                            : `Bilder + Aktivierung ok (${cc.geprueft || 0})`;
                      return (
                        <span
                          title={
                            cc.fehler
                              ? cc.fehler
                              : `${cc.geprueft || 0} Artikel geprüft` +
                                (cc.hauptvarianteAktiv ? ` · ${cc.hauptvarianteAktiv}× Hauptvariante aktiv (darf nicht sein)` : '') +
                                (cc.kindvariantenInaktiv ? ` · ${cc.kindvariantenInaktiv} mit inaktiven Varianten` : '') +
                                (cc.bilderFehlt ? ` · ${cc.bilderFehlt} ohne Bilder` : '') +
                                (cc.bilderTeilweise ? ` · ${cc.bilderTeilweise} mit unvollständigen Bildern` : '') +
                                (cc.keineKindvarianten ? ` · ${cc.keineKindvarianten} nur Hauptvariante` : '') +
                                (cc.ungeprueft ? ` · ${cc.ungeprueft} UNGEPRÜFT` : '')
                          }
                          style={{
                            fontSize: '0.72rem', padding: '2px 8px', borderRadius: 4, flexShrink: 0,
                            border: `1px solid ${cc.fehler ? '#DAD9D9' : cc.auffaellig ? '#fca5a5' : '#86efac'}`,
                            background: cc.fehler ? '#f9fafb' : cc.auffaellig ? '#fef2f2' : '#f0fdf4',
                            color: cc.fehler ? '#1A1A1A' : cc.auffaellig ? '#991B1B' : '#065F46',
                            fontWeight: cc.hauptvarianteAktiv ? 700 : 400,
                          }}>
                          {label}
                        </span>
                      );
                    })()}
                    {ts.status === 'completed' && ts.executionId && (
                      <button
                        onClick={() => setDetailExecutionId(ts.executionId!)}
                        style={{
                          padding: '2px 8px', fontSize: '0.72rem', border: '1px solid #86efac',
                          borderRadius: 4, background: '#f0fdf4', color: '#065F46', cursor: 'pointer',
                          flexShrink: 0,
                        }}>
                        Details
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Actions */}
          {isRunning && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={onCancel}
                style={{
                  padding: '8px 18px', fontSize: '0.875rem',
                  border: '1px solid #ef4444', borderRadius: 6,
                  background: '#fff', color: '#ef4444', cursor: 'pointer',
                }}>
                Abbrechen nach aktuellem Teamshop
              </button>
            </div>
          )}

          {/* Done summary */}
          {isDone && (
            <div style={{
              padding: '16px 20px', borderRadius: 8,
              background: wasServerRestart ? '#fffbeb' : progress.failedCount === 0 ? '#f0fdf4' : '#fef2f2',
              border: `1px solid ${wasServerRestart ? '#fde68a' : progress.failedCount === 0 ? '#86efac' : '#fca5a5'}`,
              marginTop: 8,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: wasServerRestart ? '#92400e' : progress.failedCount === 0 ? '#065F46' : '#991B1B' }}>
                {wasServerRestart
                  ? 'Lauf durch Server-Neustart unterbrochen'
                  : progress.status === 'cancelled' ? 'Batch abgebrochen'
                  : progress.failedCount === 0 ? 'Batch erfolgreich abgeschlossen'
                  : 'Batch mit Fehlern abgeschlossen'}
              </div>

              {wasServerRestart ? (
                <p style={{ margin: '0 0 4px', fontSize: '0.85rem', color: '#92400e', lineHeight: 1.5 }}>
                  Der Server wurde während des Laufs neu gestartet (z. B. durch ein Deployment) — deshalb
                  wurde abgebrochen, <strong>nicht wegen eines Fehlers im Teamshop</strong>. Es wurde nichts
                  beschädigt. Starte den Teamshop einfach erneut: Der Vorgang ist idempotent — bereits
                  angelegte Artikel werden wiederverwendet, nicht doppelt erstellt.
                </p>
              ) : (
                <div style={{ fontSize: '0.85rem', color: '#4b5563', display: 'flex', gap: 16 }}>
                  <span>{progress.completedCount} erfolgreich</span>
                  {progress.failedCount > 0 && <span style={{ color: '#991B1B' }}>{progress.failedCount} fehlgeschlagen</span>}
                </div>
              )}

              <button
                className="btn btn-primary"
                style={{ marginTop: 12 }}
                onClick={onNewBatch}>
                {wasServerRestart ? 'Teamshop erneut anlegen' : 'Neuen Batch starten'}
              </button>
            </div>
          )}
        </>
      )}

      {/* FlowExecutionDetail Overlay */}
      {detailExecutionId && (
        <div style={{
          position: 'fixed', inset: 0, background: '#fff', zIndex: 50, overflowY: 'auto',
        }}>
          <FlowExecutionDetail
            executionId={detailExecutionId}
            onBack={() => setDetailExecutionId(null)}
          />
        </div>
      )}

      {/* Cancel confirm dialog */}
      {showCancelConfirm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '24px 28px', maxWidth: 380, width: '90%' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1rem' }}>Batch abbrechen?</h3>
            <p style={{ margin: '0 0 20px', fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.5 }}>
              Der aktuelle Teamshop wird noch fertig bearbeitet. Danach stoppt der Batch.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={onCancelDismiss} disabled={cancelling}>
                Weiter laufen lassen
              </button>
              <button
                onClick={onCancelConfirm}
                disabled={cancelling}
                style={{
                  padding: '8px 16px', border: '1px solid #ef4444', borderRadius: 6,
                  background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '0.875rem',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {cancelling ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Wird abgebrochen…</> : 'Ja, abbrechen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =========================================================================
// Main Page
// =========================================================================

export default function TeamshopArtikelanlageApp() {
  // Wizard state — genau ein Teamshop, linearer Ablauf (kein Batch/Anzahl-Schritt mehr).
  const [step, setStep] = useState<WizardStep>('parameter');
  const [teamshop, setTeamshop] = useState<TeamshopConfig>(createEmptyTeamshop());
  const [dryRun, setDryRun] = useState(false);

  // Kategorie-Vorschlaege fuer Schritt 1 (CAT-VORSCHLAG): Kategorienliste wird EINMALIG
  // beim ersten Fokus des Name-Feldes geladen (528 Eintraege, Stand 2026-08-19) und
  // danach clientseitig gefiltert — kein Request pro Tastendruck. Gleiches Muster wie der
  // Kategorie-Typeahead im PoolManager.
  const [categories, setCategories] = useState<CategoryListItem[]>([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);

  // Shared article state (loaded once)
  const [articles, setArticles] = useState<PreviewItem[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(false);

  // Druckaufpreis-Eigenschaften (115-118), live geladen (loaded once)
  const [sponsorProperties, setSponsorProperties] = useState<SponsorProperty[]>([]);
  const [loadingSponsorProperties, setLoadingSponsorProperties] = useState(false);

  // Rabatt-Rueckrechnung fuer bestehende Teamshops (Schritt 1). Bewusst auf Knopfdruck und
  // nicht automatisch: der Lauf liest bis zu 10 Live-Preise aus Plenty, das gehoert nicht an
  // jeden Tastendruck im Kategorie-Feld.
  const [rabattVorschlag, setRabattVorschlag] = useState<RabattVorschlagDaten | null>(null);
  const [rabattLaeuft, setRabattLaeuft] = useState(false);
  const [rabattKategorieId, setRabattKategorieId] = useState<string>('');
  // Fuer welche Kategorie-ID der Lauf schon angestossen wurde — auch bei Fehlschlag gesetzt,
  // damit ein kaputter Read nicht in einer Endlosschleife neu versucht wird.
  const [rabattVersuchtFuer, setRabattVersuchtFuer] = useState<string>('');

  // Batch progress state
  const [batchId, setBatchId] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [currentExecId, setCurrentExecId] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [starting, setStarting] = useState(false);

  // Core-Hook fuer aktuelle Flow-Execution (ersetzt manuelles Polling)
  const { execution: currentExecution } = useFlowExecution(currentExecId || undefined);

  // Recovery: check localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(BATCH_ID_KEY);
    // Validate: must be a valid UUID, not "undefined" or empty
    if (saved && saved !== 'undefined' && saved !== 'null' && saved.length > 8) {
      setBatchId(saved);
      setStep('progress');
    } else if (saved) {
      // Clean up invalid entry
      localStorage.removeItem(BATCH_ID_KEY);
    }
  }, []);

  // Batch-Status laden (wird per Intervall alle 3s aufgerufen, s.u.)
  const fetchBatchStatus = useCallback(async () => {
    if (!batchId) return;
    try {
      const raw = await api.get(`/programmes/teamshop-artikelanlage/batch/${batchId}`);

      // Map API response → BatchProgress
      const data = raw.data?.data;
      const batch = data?.batch || data;
      const executions: any[] = data?.executions || [];
      const currentExec = data?.currentExecution;

      // Aktuelle Execution-ID fuer Core-Hook setzen
      if (currentExec?.id) {
        setCurrentExecId(currentExec.id);
      } else {
        setCurrentExecId(null);
      }

      // Counter primaer aus Batch-Feldern, Fallback auf Execution-Zaehlung
      const completedCount = batch.succeededShops ?? executions.filter((e: any) => e.status === 'completed').length;
      const failedCount = batch.failedShops ?? executions.filter((e: any) => e.status === 'failed').length;

      const progress: BatchProgress = {
        batchId: batch.id || batchId,
        status: batch.status || 'running',
        total: (batch.totalShops ?? executions.length) || 1,
        completedCount,
        failedCount,
        current: currentExec ? {
          index: batch.currentShopIndex ?? 0,
          name: currentExec.params?.name || `Teamshop ${(batch.currentShopIndex ?? 0) + 1}`,
          stepName: currentExec.stepStates?.find((s: any) => s.status === 'running')?.name || '',
          stepIndex: currentExec.stepStates?.filter((s: any) => s.status === 'completed').length || 0,
          stepTotal: currentExec.stepStates?.length || 6,
        } : null,
        // Per-Item results aus dem Batch-Run verwenden (wenn vorhanden)
        teamshops: (batch.results && Array.isArray(batch.results) && batch.results.length > 0)
          ? batch.results.map((r: any, i: number) => ({
              index: i,
              name: r.name || `Teamshop ${i + 1}`,
              status: r.status === 'success' ? 'completed' : r.status === 'timeout' ? 'timeout' : r.status === 'skipped' ? 'skipped' : r.status === 'failed' ? 'failed' : 'pending',
              executionId: r.executionId,
              error: r.error,
              priceCheck: r.priceCheck,
            }))
          : executions.map((e: any, i: number) => ({
              index: i,
              name: e.params?.name || `Teamshop ${i + 1}`,
              status: e.status === 'running' ? 'running' : e.status === 'completed' ? 'completed' : e.status === 'failed' ? 'failed' : 'pending',
              executionId: e.id,
              error: e.errorMessage,
            })),
      };

      setBatchProgress(progress);
      if (['completed', 'failed', 'cancelled', 'partial_failure'].includes(progress.status)) {
        localStorage.removeItem(BATCH_ID_KEY);
      }
    } catch (err: any) {
      // Bei 500er (ungueltige Batch-ID) → Recovery abbrechen
      if (err?.response?.status === 500 || err?.response?.status === 404) {
        localStorage.removeItem(BATCH_ID_KEY);
        setBatchId(null);
        setStep('parameter');
      }
    }
  }, [batchId]);

  // Initial fetch wenn Progress-Step betreten wird
  useEffect(() => {
    if (step !== 'progress' || !batchId) return;
    fetchBatchStatus();
  }, [step, batchId, fetchBatchStatus]);

  // Kontinuierliches Polling alle 3s, solange der Progress-Step offen und der Batch NICHT
  // terminal ist. Robuster als die einmalige Ladung: fängt die Race ab, dass beim Betreten
  // von 'progress' die erste Flow-Execution noch nicht registriert war (Batch legt sie erst
  // ~Sekunden nach dem Anlegen an → sonst blieb der Screen bei 0/1 mit leerer Liste stehen).
  useEffect(() => {
    if (step !== 'progress' || !batchId) return;
    const terminal = ['completed', 'failed', 'cancelled', 'partial_failure'];
    if (batchProgress && terminal.includes(batchProgress.status)) return;
    const iv = setInterval(() => { fetchBatchStatus(); }, 3000);
    return () => clearInterval(iv);
  }, [step, batchId, batchProgress?.status, fetchBatchStatus]);

  // Batch-Status neu laden wenn aktuelle Execution abgeschlossen ist
  // (Backend startet dann naechste Execution)
  useEffect(() => {
    if (!currentExecution) return;
    const status = currentExecution.status;
    if (status === 'completed' || status === 'failed' || status === 'cancelled') {
      // Kurze Verzoegerung damit Backend naechste Execution starten kann
      const timeout = setTimeout(() => {
        setCurrentExecId(null);
        fetchBatchStatus();
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [currentExecution?.status, fetchBatchStatus]);

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  const updateTeamshopField = (field: keyof Omit<TeamshopConfig, 'selectedVariants' | 'druckProperties'>, value: string) => {
    setTeamshop(prev => ({ ...prev, [field]: value }));
  };

  // -----------------------------------------------------------------------
  // Kategorie-Vorschlaege (Schritt 1)
  // -----------------------------------------------------------------------

  /** Kategorienliste einmalig holen (GET /pool/categories, rein lesend). */
  const loadCategories = useCallback(async () => {
    if (categoriesLoaded || loadingCategories) return;
    setLoadingCategories(true);
    try {
      const raw = await api.get('/programmes/teamshop-artikelanlage/pool/categories');
      setCategories(raw.data?.data?.categories ?? []);
      setCategoriesLoaded(true);
    } catch (err: any) {
      // Nur Hinweis, kein Blocker: die Kategorie-ID bleibt manuell eintragbar.
      toast.error(`Kategorien laden fehlgeschlagen: ${err?.response?.data?.error?.message || err?.message || 'Unbekannter Fehler'}`);
    } finally {
      setLoadingCategories(false);
    }
  }, [categoriesLoaded, loadingCategories]);

  const nameSuggestions = useMemo(
    () => suggestCategories(categories, teamshop.name),
    [categories, teamshop.name],
  );

  /**
   * Zur aktuell eingetragenen Kategorie-ID gehoerende Kategorie — einzige Quelle der
   * Bestaetigungsanzeige. Bewusst aus dem ID-Feld abgeleitet (nicht als eigener State),
   * damit manuelles Tippen und Vorschlag-Klick denselben Pfad nehmen.
   * null heisst NICHT "ungueltig" (Registry-Snapshot <=24h) -> nie als Blocker verwenden.
   */
  const resolvedCategory = useMemo(
    () => findCategoryById(categories, teamshop.kategorieId),
    [categories, teamshop.kategorieId],
  );

  const showNameMismatchHint = !!resolvedCategory
    && teamshop.name.trim() !== ''
    && namesDiffer(teamshop.name, resolvedCategory.leaf);

  const handleSelectCategory = useCallback((cat: CategorySuggestion) => {
    updateTeamshopField('kategorieId', String(cat.categoryId));
    setShowNameSuggestions(false);
  }, []);

  const updateSelectedVariants = (map: Map<number, Set<number>>) => {
    setTeamshop(prev => ({ ...prev, selectedVariants: map }));
  };

  const updateDruckProperties = (map: Map<number, Set<number>>) => {
    setTeamshop(prev => ({ ...prev, druckProperties: map }));
  };

  const loadArticles = async () => {
    if (loadingArticles) return;
    setLoadingArticles(true);
    try {
      const res = await api.get('/programmes/teamshop-artikelanlage/preview-items');
      const loaded: PreviewItem[] = res.data?.data?.items ?? res.data?.data ?? [];
      setArticles(loaded);
      // Auswahl mit der frischen Liste zusammenfuehren (mergeVariantSelection, getestet):
      // neue Artikel werden komplett vorselektiert, eine bereits getroffene Auswahl bleibt
      // erhalten — auch eine bewusste Komplett-Abwahl. Vorher wurde hier pauschal alles neu
      // vorselektiert, wodurch jeder Rueckschritt nach Schritt 2 die Farbauswahl frass.
      setTeamshop(prev => ({
        ...prev,
        selectedVariants: mergeVariantSelection(loaded, prev.selectedVariants),
        druckProperties: pruneItemMap(prev.druckProperties, loaded.map(i => i.itemId)),
      }));
      toast.success(`${loaded.length} Artikel geladen`);
    } catch {
      toast.error('Fehler beim Laden der Artikel');
    } finally {
      setLoadingArticles(false);
    }
  };

  const ladeRabattVorschlag = async () => {
    const katId = teamshop.kategorieId.trim();
    if (katId === '' || rabattLaeuft) return;
    setRabattLaeuft(true);
    setRabattVersuchtFuer(katId);
    setRabattVorschlag(null);
    try {
      const res = await api.get('/programmes/teamshop-artikelanlage/rabatt-vorschlag', {
        params: { categoryId: katId },
      });
      const daten: RabattVorschlagDaten = res.data?.data;
      setRabattVorschlag(daten);
      setRabattKategorieId(katId);
      // Vorbelegen nur, wenn das Feld leer ist — eine bereits eingetippte Zahl wird NIE
      // ueberschrieben; der Vorschlag bleibt als Knopf daneben stehen.
      if (daten?.vorschlagPct != null && teamshop.rabatt.trim() === '') {
        updateTeamshopField('rabatt', String(daten.vorschlagPct));
      }
      if (daten?.vorschlagPct == null) {
        toast('Kein eindeutiger Rabatt erkennbar — bitte selbst eintragen');
      }
    } catch {
      toast.error('Rabatt-Rückrechnung fehlgeschlagen');
    } finally {
      setRabattLaeuft(false);
    }
  };

  // Rabatt automatisch zurueckrechnen, sobald die Kategorie-ID steht — analog zum
  // Kategorie-Vorschlag, der ebenfalls von selbst kommt.
  //
  // BEWUSST entprellt und genau EINMAL je ID: Der Lauf liest bis zu 10 Live-Preise aus Plenty.
  // Ohne Entprellung feuerte er beim Tippen von "339" dreimal (3 / 33 / 339). Die 1200 ms sind
  // der Kompromiss — lang genug, dass niemand mitten in einer Zahl ausgeloest wird, kurz genug,
  // dass der Vorschlag da ist, bevor man zum naechsten Feld greift. Der Knopf bleibt als
  // "nochmal rechnen" bestehen (z.B. nachdem die Preise in Plenty geaendert wurden).
  useEffect(() => {
    const katId = teamshop.kategorieId.trim();
    if (katId.length < 2 || !Number.isFinite(Number(katId))) return;
    if (katId === rabattVersuchtFuer || rabattLaeuft) return;

    const timer = window.setTimeout(() => { void ladeRabattVorschlag(); }, 1200);
    return () => window.clearTimeout(timer);
    // ladeRabattVorschlag absichtlich nicht in den Deps: die Funktion wird bei jedem Render neu
    // erzeugt, das wuerde den Timer bei jedem Tastendruck im Namensfeld zuruecksetzen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamshop.kategorieId, rabattVersuchtFuer, rabattLaeuft]);

  const loadSponsorProperties = async () => {
    if (loadingSponsorProperties) return;
    setLoadingSponsorProperties(true);
    try {
      const res = await api.get('/programmes/teamshop-artikelanlage/sponsor-properties');
      const loaded: SponsorProperty[] = res.data?.data?.properties ?? [];
      setSponsorProperties(loaded);
    } catch {
      toast.error('Fehler beim Laden der Druckaufpreis-Eigenschaften');
    } finally {
      setLoadingSponsorProperties(false);
    }
  };

  // Schritt 2 → 3: Pool wurde übernommen (flagOne=29 gesetzt) → Farben & Veredelung laden.
  const handlePoolProceed = () => {
    setStep('colors');
    // Artikel immer frisch laden, damit exakt die eben übernommenen (flagOne=29) erscheinen.
    // Die Auswahl wird dabei NICHT mehr verworfen — loadArticles fuehrt sie ueber
    // mergeVariantSelection mit der neuen Liste zusammen. setArticles([]) bleibt, damit im
    // Panel waehrend des Ladens nicht die alte Liste stehen bleibt.
    setArticles([]);
    loadArticles();
    if (sponsorProperties.length === 0) loadSponsorProperties();
  };

  const handleStartBatch = async () => {
    setStarting(true);
    try {
      // Backend bleibt unverändert: wir schicken einen Batch mit genau EINEM Teamshop.
      const t = teamshop;
      const items = [{
        name: t.name.trim(),
        kategorieId: Number(t.kategorieId),
        rabatt: Number(t.rabatt),
        ...(t.tagIds.trim() ? { tagIds: t.tagIds.trim() } : {}),
        selectedItems: articles
          .filter(a => (t.selectedVariants.get(a.itemId)?.size ?? 0) > 0)
          .map(a => ({
            itemId: a.itemId,
            variationIds: [...(t.selectedVariants.get(a.itemId) || [])],
            druckPropertyIds: [...(t.druckProperties.get(a.itemId) || [])],
          })),
      }];

      const res = await api.post('/programmes/teamshop-artikelanlage/batch', { items, dryRun });
      const newBatchId = res.data?.data?.batchId;
      if (!newBatchId || newBatchId === 'undefined') {
        throw new Error('Keine gültige Batch-ID erhalten');
      }
      setBatchId(newBatchId);
      localStorage.setItem(BATCH_ID_KEY, newBatchId);
      setStep('progress');
    } catch (err: any) {
      toast.error(`Start fehlgeschlagen: ${err?.message ?? 'Unbekannter Fehler'}`);
    } finally {
      setStarting(false);
    }
  };

  const handleCancelBatch = async () => {
    if (!batchId) return;
    setCancelling(true);
    try {
      await api.post(`/programmes/teamshop-artikelanlage/batch/${batchId}/cancel`);
      setShowCancelConfirm(false);
      toast.success('Abbruch angefordert');
    } catch {
      toast.error('Abbruch fehlgeschlagen');
    } finally {
      setCancelling(false);
    }
  };

  const handleNewBatch = () => {
    setBatchId(null);
    setBatchProgress(null);
    setStep('parameter');
    setTeamshop(createEmptyTeamshop());
    setArticles([]);
  };

  // -----------------------------------------------------------------------
  // Validation
  // -----------------------------------------------------------------------

  const isParameterValid =
    teamshop.name.trim() !== '' &&
    teamshop.kategorieId.trim() !== '' &&
    teamshop.rabatt.trim() !== '';

  const hasVariantsSelected = [...teamshop.selectedVariants.values()].some(s => s.size > 0);
  const selectedItemCount = [...teamshop.selectedVariants.values()].filter(s => s.size > 0).length;
  const selectedVariantTotal = [...teamshop.selectedVariants.values()].reduce((sum, s) => sum + s.size, 0);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>

      {/* Header + Schritt-Anzeige (Stepper) — im Fortschritt ausgeblendet */}
      {step !== 'progress' && (
      <div style={{ marginBottom: 20 }}>
        <button
          onClick={() => {
            if (step === 'parameter') {
              if (window.history.length > 1) window.history.back();
              else window.location.href = '/';
            } else if (step === 'articles') setStep('parameter');
            else if (step === 'colors') setStep('articles');
            else if (step === 'review') setStep('colors');
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#6b7280', fontSize: '0.8rem', padding: 0, marginBottom: 8,
          }}>
          <ArrowLeft size={14} /> {step === 'parameter' ? 'Dashboard' : 'Zurück'}
        </button>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
          <ShoppingBag size={20} style={{ color: '#e9ff57' }} />
          Teamshop Artikelanlage
          {teamshop.name.trim() && (
            <span style={{ fontSize: '0.9rem', fontWeight: 400, color: '#6b7280' }}>
              — {teamshop.name.trim()}
            </span>
          )}
        </h2>
        <p style={{ margin: '5px 0 16px', color: '#6b7280', fontSize: '0.875rem' }}>
          Quellartikel auswählen und als neuen Teamshop in PlentyMarkets anlegen
        </p>

        {/* Stepper: 1 Teamshop · 2 Artikel · 3 Farben & Veredelung · 4 Übersicht */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          {STEP_LABELS.map((s, i) => {
            const currentIndex = STEP_LABELS.findIndex(x => x.key === step);
            const done = i < currentIndex;
            const active = i === currentIndex;
            const clickable = i < currentIndex;
            return (
              <React.Fragment key={s.key}>
                {i > 0 && (
                  <div style={{ width: 26, height: 2, background: done || active ? '#c8dd40' : '#e5e7eb' }} />
                )}
                <button
                  onClick={() => { if (clickable) setStep(s.key); }}
                  disabled={!clickable}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '6px 12px', borderRadius: 20, border: 'none',
                    background: active ? '#c8dd40' : 'transparent',
                    cursor: clickable ? 'pointer' : 'default',
                  }}>
                  <span style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.75rem', fontWeight: 700,
                    background: active ? '#1a1a1a' : done ? '#c8dd40' : '#e5e7eb',
                    color: active ? '#fff' : done ? '#1a1a1a' : '#9ca3af',
                  }}>
                    {done ? <Check size={13} /> : i + 1}
                  </span>
                  <span style={{
                    fontSize: '0.82rem', fontWeight: active ? 700 : 500,
                    color: active ? '#1a1a1a' : done ? '#374151' : '#9ca3af',
                  }}>
                    {s.label}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
      )}

      {/* ===== SCHRITT 1: TEAMSHOP / VEREIN ===== */}
      {step === 'parameter' && (
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          <div className="card" style={{ margin: 0 }}>
            <div className="card-header">
              <h3 className="card-title" style={{ fontSize: '0.95rem', margin: 0 }}>Teamshop / Verein</h3>
            </div>

            {/* Name-Feld mit Kategorie-Typeahead: Vereinsname tippen -> passende Kategorien
                inkl. ID vorschlagen. Kategoriename und Teamshop-Name sind fachlich identisch. */}
            <div className="form-group" style={{ position: 'relative' }}>
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Name *</label>
              <input
                type="text" className="form-input" placeholder="z.B. SV Ohrbeck"
                value={teamshop.name}
                onChange={e => {
                  updateTeamshopField('name', e.target.value);
                  setShowNameSuggestions(true);
                }}
                onFocus={() => { loadCategories(); setShowNameSuggestions(true); }}
                onBlur={() => { window.setTimeout(() => setShowNameSuggestions(false), 150); }}
              />

              {showNameSuggestions && teamshop.name.trim().length >= MIN_QUERY_LENGTH && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
                  background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                  maxHeight: 240, overflowY: 'auto', boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                }}>
                  {loadingCategories ? (
                    <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: '#9ca3af' }}>Lädt Kategorien…</div>
                  ) : nameSuggestions.length === 0 ? (
                    <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: '#9ca3af' }}>
                      Keine passende Kategorie — Kategorie-ID unten manuell eintragen
                    </div>
                  ) : (
                    nameSuggestions.map(cat => (
                      <div
                        key={cat.categoryId}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => handleSelectCategory(cat)}
                        style={{
                          padding: '8px 12px', fontSize: '0.85rem', color: '#1a1a1a', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                        }}>
                        <span>
                          {cat.leaf}
                          {cat.label !== cat.leaf && (
                            <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}> · {cat.label}</span>
                          )}
                        </span>
                        <span style={{
                          flexShrink: 0, fontSize: '0.75rem', fontWeight: 700, color: '#1a1a1a',
                          background: '#e9ff57', borderRadius: 4, padding: '1px 6px',
                        }}>
                          {cat.categoryId}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Abweichung Name <-> Kategoriename: nur Hinweis, kein stilles Ueberschreiben —
                  der Name landet in den erzeugten Artikeln (User-Entscheidung 2026-08-19). */}
              {showNameMismatchHint && resolvedCategory && (
                <div style={{
                  marginTop: 6, fontSize: '0.75rem', color: '#6b7280',
                  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                }}>
                  <span>Kategorie heißt „{resolvedCategory.leaf}"</span>
                  <button
                    type="button"
                    onClick={() => updateTeamshopField('name', resolvedCategory.leaf)}
                    style={{
                      border: '1px solid #d1d5db', background: '#f9fafb', borderRadius: 4,
                      padding: '2px 8px', fontSize: '0.72rem', color: '#1a1a1a', cursor: 'pointer',
                    }}>
                    Namen übernehmen
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Kategorie ID *</label>
              <input
                type="number" className="form-input" placeholder="z.B. 684"
                value={teamshop.kategorieId}
                onChange={e => updateTeamshopField('kategorieId', e.target.value)}
              />
              {/* Bestaetigung der ID. Unbekannte ID ist KEIN Fehler: die Registry ist ein
                  Snapshot (<=24h), eine heute angelegte Kategorie fehlt darin noch. */}
              {teamshop.kategorieId.trim() !== '' && categoriesLoaded && (
                resolvedCategory ? (
                  <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#4b7c1f', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Check size={12} />
                    {resolvedCategory.label}
                  </div>
                ) : (
                  <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#9ca3af' }}>
                    ID nicht in der Kategorieliste — falls die Kategorie heute angelegt wurde, ist das normal
                  </div>
                )
              )}
            </div>

            <div className="form-group">
              <label className="form-label" style={{ fontSize: '0.85rem' }}>Rabatt % *</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <input
                  type="number" className="form-input" placeholder="z.B. 15"
                  value={teamshop.rabatt}
                  onChange={e => updateTeamshopField('rabatt', e.target.value)}
                  style={{ flex: 1 }}
                />
                {/* Nur sinnvoll bei einem BESTEHENDEN Shop — ohne Kategorie gibt es nichts
                    zurueckzurechnen. Deshalb an die Kategorie-ID gekoppelt. */}
                <button
                  className="btn btn-secondary"
                  onClick={ladeRabattVorschlag}
                  disabled={teamshop.kategorieId.trim() === '' || rabattLaeuft}
                  style={{ whiteSpace: 'nowrap' }}
                  title="Analysiert die bestehenden Artikel dieses Teamshops und ermittelt den verwendeten Rabatt">
                  {rabattLaeuft
                    ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Prüft…</>
                    : <><RefreshCw size={14} /> {rabattVorschlag ? 'Erneut analysieren' : 'Analysiere Shoprabatt'}</>
                  }
                </button>
              </div>

              {teamshop.kategorieId.trim() === '' && (
                <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#9ca3af' }}>
                  Sobald die Kategorie-ID eines bestehenden Teamshops steht, wird dessen
                  Shoprabatt automatisch analysiert.
                </div>
              )}

              {rabattVorschlag && rabattKategorieId === teamshop.kategorieId.trim() && (
                <div style={{ marginTop: 8, fontSize: '0.78rem' }}>
                  {rabattVorschlag.vorschlagPct != null ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ color: '#4b7c1f', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <Check size={12} />
                        Gecheckt: <strong>{rabattVorschlag.vorschlagPct} %</strong>
                      </span>
                      <span style={{ color: '#9ca3af' }}>
                        aus {rabattVorschlag.gestuetztVon} von {rabattVorschlag.auswertbar} Artikeln
                        {' '}({rabattVorschlag.verlaesslichkeit === 'hoch' ? 'eindeutig' : 'mit Abweichungen'})
                      </span>
                      {String(rabattVorschlag.vorschlagPct) !== teamshop.rabatt.trim() && (
                        <button
                          onClick={() => updateTeamshopField('rabatt', String(rabattVorschlag.vorschlagPct))}
                          style={{
                            background: '#e9ff57', color: '#1a1a1a', border: 'none', borderRadius: 4,
                            padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 600,
                          }}>
                          übernehmen
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ color: '#9ca3af' }}>
                      Kein eindeutiger Rabatt erkennbar ({rabattVorschlag.auswertbar} Artikel geprüft) —
                      bitte selbst eintragen.
                    </div>
                  )}

                  {rabattVorschlag.nurGeteilteArtikel && (
                    <div style={{ marginTop: 4, color: '#9a6b00' }}>
                      Achtung: nur geteilte „Teamshop Basics"-Artikel gefunden. Deren Preis gilt
                      shopübergreifend und muss nicht der Rabatt dieses Shops sein.
                    </div>
                  )}

                  {/* Belege offenlegen — der Vorschlag soll nachprüfbar sein, nicht geglaubt werden. */}
                  {rabattVorschlag.stichproben.length > 0 && (
                    <details style={{ marginTop: 6 }}>
                      <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
                        Grundlage anzeigen ({rabattVorschlag.stichproben.length} Artikel)
                      </summary>
                      <div style={{ overflowX: 'auto', marginTop: 6 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.73rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                              {['Artikel', 'Nr.', 'Shop-Preis', 'Aufpreis', 'UVP Quelle', 'ergibt'].map(h => (
                                <th key={h} style={{ padding: '4px 8px', textAlign: 'left', fontWeight: 600 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {rabattVorschlag.stichproben.map(sp => (
                              <tr key={sp.variationId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '4px 8px' }}>
                                  {sp.name}
                                  {sp.geteilt && <span style={{ color: '#9a6b00' }}> (geteilt)</span>}
                                </td>
                                <td style={{ padding: '4px 8px', color: '#6b7280' }}>{sp.number}</td>
                                <td style={{ padding: '4px 8px' }}>{sp.grund ? '—' : formatEuro(sp.webPreis)}</td>
                                <td style={{ padding: '4px 8px' }}>{sp.aufpreis > 0 ? formatEuro(sp.aufpreis) : '—'}</td>
                                <td style={{ padding: '4px 8px' }}>{sp.uvpQuelle ? formatEuro(sp.uvpQuelle) : '—'}</td>
                                <td style={{ padding: '4px 8px', color: sp.grund ? '#9ca3af' : '#1a1a1a' }}>
                                  {sp.grund
                                    ? sp.grund
                                    : (sp.kandidaten ?? []).map(k => `${k.toFixed(1)} %`).join(' / ')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p style={{ margin: '6px 0 0', color: '#9ca3af' }}>
                        Mehrere Werte je Artikel = mehrere Lesarten (Aufpreis nach dem Rabatt wie heute,
                        oder Veredelungs-Zuschlag vor dem Rabatt wie in älteren Shops). Gecheckt wird der
                        Wert, auf den sich die meisten Artikel einigen.
                      </p>
                    </details>
                  )}
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label" style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Tag size={13} />
                Tag IDs
                <span style={{ color: '#9ca3af', fontWeight: 400, fontSize: '0.75rem' }}>(optional)</span>
              </label>
              <input
                type="text" className="form-input" placeholder="z.B. 5,12,18"
                value={teamshop.tagIds}
                onChange={e => updateTeamshopField('tagIds', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
            <button
              className="btn btn-primary"
              disabled={!isParameterValid}
              style={{ opacity: isParameterValid ? 1 : 0.45, cursor: isParameterValid ? 'pointer' : 'not-allowed' }}
              onClick={() => setStep('articles')}>
              Weiter: Artikel auswählen <ChevronRight size={15} />
            </button>
          </div>
          {!isParameterValid && (
            <p style={{ textAlign: 'right', marginTop: 6, fontSize: '0.73rem', color: '#9ca3af' }}>
              Name, Kategorie-ID und Rabatt ausfüllen
            </p>
          )}
        </div>
      )}

      {/* ===== SCHRITT 2: ARTIKEL AUSWÄHLEN (Quellartikel-Pool, setzt flagOne=29) ===== */}
      {/* Schritt 2 wird bewusst IMMER gerendert und nur ausgeblendet, statt beim Schrittwechsel
          aus dem Baum zu fliegen. Der PoolManager haelt seinen kompletten Zustand intern
          (Suchbegriff, Suchmodus, Trefferliste, Artikelauswahl, Reconciliation) — beim Unmount
          war der weg, und wer aus Schritt 3/4 zurueckkam, stand vor einer leeren Suche.
          Das kostet nichts: der PoolManager hat genau einen useEffect (Polling-Cleanup) und
          laedt von sich aus nichts beim Mount. Laeuft gerade ein Pool-Apply, poll er im
          Hintergrund weiter — das ist erwuenscht. */}
      <div style={{ display: step === 'articles' ? 'block' : 'none' }}>
        <PoolManager onProceed={handlePoolProceed} />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
          <button className="btn btn-secondary" onClick={() => setStep('parameter')}>
            <ChevronLeft size={15} /> Zurück
          </button>
          {/* Zweiter Weiter-Weg OHNE Plenty-Write: nutzt den aktuell markierten flagOne=29-Pool.
              Nötig auf Dev (Writes gesperrt) und sinnvoll in Prod, wenn der Pool bereits passt. */}
          <button className="btn btn-secondary" onClick={handlePoolProceed}>
            Aktuellen Pool verwenden <ChevronRight size={15} />
          </button>
        </div>
        <p style={{ marginTop: 8, fontSize: '0.73rem', color: '#9ca3af' }}>
          Zum <strong>Ändern</strong> des Pools: Artikel in der Trefferliste auswählen und oben auf
          „Artikel übernehmen &amp; weiter" klicken (schreibt <code>flag 29</code> in Plenty). Willst du den
          Pool nicht ändern — oder testest auf Dev, wo Plenty-Writes gesperrt sind — nimm
          „Aktuellen Pool verwenden".
        </p>
      </div>

      {/* ===== SCHRITT 3: FARBEN & VEREDELUNG ===== */}
      {step === 'colors' && (
        <>
          <ArticleSelectorPanel
            items={articles}
            loadingItems={loadingArticles}
            selectedVariants={teamshop.selectedVariants}
            onSelectedVariantsChange={updateSelectedVariants}
            onLoadItems={loadArticles}
            sponsorProperties={sponsorProperties}
            druckProperties={teamshop.druckProperties}
            onDruckPropertiesChange={updateDruckProperties}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button className="btn btn-secondary" onClick={() => setStep('articles')}>
              <ChevronLeft size={15} /> Zurück
            </button>
            <button
              className="btn btn-primary"
              disabled={!hasVariantsSelected}
              style={{ opacity: hasVariantsSelected ? 1 : 0.45, cursor: hasVariantsSelected ? 'pointer' : 'not-allowed' }}
              onClick={() => setStep('review')}>
              Weiter: Übersicht <ChevronRight size={15} />
            </button>
          </div>
          {!hasVariantsSelected && (
            <p style={{ textAlign: 'right', marginTop: 6, fontSize: '0.73rem', color: '#9ca3af' }}>
              Mindestens eine Variante auswählen
            </p>
          )}
        </>
      )}

      {/* ===== SCHRITT 4: ÜBERSICHT ===== */}
      {step === 'review' && (
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div className="card" style={{ margin: '0 0 16px' }}>
            <div className="card-header">
              <h3 className="card-title" style={{ fontSize: '0.95rem', margin: 0 }}>Zusammenfassung</h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  {['Name', 'Kat.-ID', 'Rabatt', 'Tags', 'Artikel', 'Varianten'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1a1a1a' }}>{teamshop.name}</td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>{teamshop.kategorieId}</td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>{teamshop.rabatt}%</td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>{teamshop.tagIds.trim() || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: '#f0fdf4', color: '#065F46', padding: '2px 8px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600 }}>
                      {selectedItemCount}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: '#eff6ff', color: '#1e40af', padding: '2px 8px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600 }}>
                      {selectedVariantTotal}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Preis-Transparenz je Artikel (UI-01): Basis-Webpreis + Aufpreise + Gesamtpreis. */}
          {articles.length > 0 && (() => {
            const selectedArticles = articles.filter(a => (teamshop.selectedVariants.get(a.itemId)?.size ?? 0) > 0);
            if (selectedArticles.length === 0) return null;
            const rabatt = Number(teamshop.rabatt) || 0;
            return (
              <div className="card" style={{ margin: '0 0 16px' }}>
                <div className="card-header">
                  <h3 className="card-title" style={{ fontSize: '0.95rem', margin: 0 }}>Preis-Transparenz</h3>
                </div>
                <div style={{ padding: '10px 16px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {selectedArticles.map(a => {
                    const uvp = extractUvpFrontend(a);
                    const basePrice = computeMainPriceDisplay(uvp, rabatt);
                    const selectedPropIds = [...(teamshop.druckProperties.get(a.itemId) || [])];
                    const surchargeRows = selectedPropIds
                      .map(pid => sponsorProperties.find(sp => sp.propertyId === pid))
                      .filter((sp): sp is SponsorProperty => !!sp);
                    const surchargeSum = surchargeRows.reduce((sum, sp) => sum + sp.surcharge, 0);
                    const total = basePrice + surchargeSum;
                    return (
                      <div key={a.itemId} style={{ border: '1px solid #f3f4f6', borderRadius: 6, padding: '8px 12px' }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>
                          {a.name}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span>
                            Webshop-Preis <span style={{ color: '#9ca3af' }}>(aus UVP {formatEuro(uvp)} − {rabatt} % Rabatt)</span>:{' '}
                            <strong style={{ color: '#1a1a1a' }}>{formatEuro(basePrice)}</strong>
                          </span>
                          {surchargeRows.length === 0 ? (
                            <span>+ kein Druck-Aufpreis</span>
                          ) : (
                            surchargeRows.map(sp => (
                              <span key={sp.propertyId}>+ Druck {sp.name} (ID {sp.propertyId}): +{formatEuro(sp.surcharge)}</span>
                            ))
                          )}
                          <span style={{ borderTop: '1px solid #f3f4f6', marginTop: 2, paddingTop: 2 }}>
                            Gesamt-Webpreis <span style={{ color: '#9ca3af' }}>(Webshop-Preis + Druck-Aufpreise)</span>:{' '}
                            <strong style={{ color: '#1a1a1a' }}>{formatEuro(total)}</strong>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* DryRun + Start */}
          <div className="card" style={{ margin: '0 0 20px' }}>
            <div style={{ padding: '16px 20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={e => setDryRun(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: '#c8dd40' }}
                />
                <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>Testlauf</span>
              </label>
              <p style={{ margin: '4px 0 0 24px', fontSize: '0.75rem', color: '#9ca3af', lineHeight: 1.3 }}>
                Kein Schreiben in PlentyMarkets — nur zur Überprüfung
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn btn-secondary" onClick={() => setStep('colors')}>
              <ChevronLeft size={15} /> Zurück
            </button>
            <button
              className="btn btn-primary"
              disabled={starting}
              style={{ opacity: starting ? 0.6 : 1 }}
              onClick={handleStartBatch}>
              {starting
                ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Startet…</>
                : <><Play size={15} /> {dryRun ? 'Testlauf starten' : 'Teamshop anlegen'}</>
              }
            </button>
          </div>
        </div>
      )}

      {/* ===== PHASE 4: PROGRESS ===== */}
      {step === 'progress' && batchId && (
        <ProgressStep
          progress={batchProgress}
          batchId={batchId}
          currentExecId={currentExecId}
          onCancel={() => setShowCancelConfirm(true)}
          showCancelConfirm={showCancelConfirm}
          onCancelConfirm={handleCancelBatch}
          onCancelDismiss={() => setShowCancelConfirm(false)}
          cancelling={cancelling}
          onNewBatch={handleNewBatch}
        />
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
